import {
  Box,
  ClipboardList,
  Globe,
  Smartphone,
  Sparkles,
  Telescope,
  type LucideIcon,
} from 'lucide-react';
import type { JSX } from 'react';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip.js';
import { cn } from '@/lib/utils.js';
import {
  runFamily,
  runKindLabel,
  type CaseOutcome,
  type ExecutionStatus,
  type Platform,
  type PresentedCaseStatus,
  type ReapedReason,
  type ResultStatus,
  type Run,
  type RunFamily,
  type RunSummary,
  type RunStatus,
  type StageStatus,
  type TestResult,
} from '@/lib/api/quality-types.js';

import {
  OUTCOME_LABEL,
  outcomeLabel,
  outcomeProjection,
  outcomeTooltip,
  type OutcomeProjection,
} from './quality-outcome.js';

type AnyStatus =
  | RunStatus
  | ResultStatus
  | StageStatus
  | ExecutionStatus
  | PresentedCaseStatus;

type BadgeVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'success'
  | 'info'
  | 'warning';

/**
 * Maps a run/result/stage status to a host Badge variant. Uses the
 * token-backed `success`/`info` variants the badge primitive ships; no raw
 * colours. running/reporting get a soft pulse to read as "in flight".
 */
const STATUS_VARIANT: Record<AnyStatus, BadgeVariant> = {
  passed: 'success',
  failed: 'destructive',
  broken: 'destructive',
  timeout: 'warning',
  running: 'info',
  reporting: 'info',
  awaiting_review: 'warning',
  awaiting_input: 'warning',
  /** Terminal, but not a failure verdict — the same soft tone as the in-flight "needs attention"
   *  states, not the destructive tone `failed` gets. Never added to PULSING: it must not read as
   *  in-flight. */
  incomplete: 'warning',
  queued: 'outline',
  pending: 'outline',
  cancelled: 'secondary',
  completed: 'success',
  partially_completed: 'warning',
  not_completed: 'warning',
  defect_found: 'destructive',
  case_unclear: 'warning',
  blocked_on_input: 'warning',
  qa_error: 'destructive',
  infra_error: 'destructive',
  unresolved: 'warning',
  /** Not a verdict yet: neutral, and never in PULSING. */
  assessing: 'outline',
  /** Skipped on purpose by a repeated run's stop policy: neutral, never a failure tone. */
  not_run: 'outline',
};

const PULSING = new Set<AnyStatus>(['running', 'reporting']);

/**
 * The outcomes whose badge text differs from their raw status. `passed` and `cancelled` are
 * deliberately absent: the badge renders the raw status under a `capitalize` class, which already
 * reads "Passed" and "Cancelled", and putting the word here instead would change what the DOM
 * actually contains rather than only what a reader sees.
 */
const LABELLED_OUTCOMES = [
  'unresolved',
  'defect_found',
  'case_unclear',
  'blocked_on_input',
  'qa_error',
  'infra_error',
] as const satisfies readonly CaseOutcome[];

/**
 * Friendlier labels where the raw status reads poorly when capitalized. The case outcomes are taken
 * from `OUTCOME_LABEL` rather than repeated here: they are the same words in the same product, and a
 * copy means a rename has to be written twice and silently drifts when it is not. A label renders
 * exactly as written, in sentence case like the admin console; only a raw status is capitalized.
 */
const STATUS_LABEL: Partial<Record<AnyStatus, string>> = {
  awaiting_review: 'Review',
  awaiting_input: 'Needs input',
  timeout: 'Timed out',
  incomplete: 'Incomplete',
  completed: 'Completed',
  partially_completed: 'Partially completed',
  not_completed: 'Not completed',
  assessing: 'Assessing',
  not_run: outcomeLabel('not_run'),
  ...Object.fromEntries(
    LABELLED_OUTCOMES.map((outcome) => [outcome, OUTCOME_LABEL[outcome]]),
  ),
};

type OutcomeChipId = CaseOutcome | 'assessing' | 'not_run' | 'unconfirmed-qa';

/** Outcome summary chips keep the same semantic tones as the status badges, with a light fill
 * and a visible but quiet border so a result never reads like a destructive action. */
const OUTCOME_CHIP_CLASS: Record<OutcomeChipId, string> = {
  unresolved:
    'border-amber-500/30 bg-amber-500/15 text-amber-700 [[data-theme=dark]_&]:text-amber-300',
  passed: 'border-success-500/30 bg-success-500/15 text-success-600',
  defect_found:
    'border-rose-500/30 bg-rose-500/15 text-rose-700 [[data-theme=dark]_&]:text-rose-300',
  case_unclear:
    'border-orange-500/30 bg-orange-500/15 text-orange-700 [[data-theme=dark]_&]:text-orange-300',
  blocked_on_input:
    'border-sky-500/30 bg-sky-500/15 text-sky-700 [[data-theme=dark]_&]:text-sky-300',
  qa_error:
    'border-pink-500/30 bg-pink-500/15 text-pink-700 [[data-theme=dark]_&]:text-pink-300',
  infra_error:
    'border-red-500/30 bg-red-500/15 text-red-700 [[data-theme=dark]_&]:text-red-300',
  cancelled:
    'border-secondary-foreground/20 bg-secondary/60 text-secondary-foreground',
  assessing: 'border-border bg-muted/50 text-muted-foreground',
  /** Dashed and unfilled: nothing ran, so the chip carries no verdict colour. */
  not_run: 'border-dashed border-border bg-transparent text-muted-foreground',
  'unconfirmed-qa':
    'border-yellow-500/30 bg-yellow-500/15 text-yellow-700 [[data-theme=dark]_&]:text-yellow-300',
};

const OUTCOME_SEGMENT_CLASS: Record<
  CaseOutcome | 'assessing' | 'not_run',
  string
> = {
  unresolved: 'bg-amber-500',
  passed: 'bg-success-500',
  defect_found: 'bg-rose-500',
  case_unclear: 'bg-orange-500',
  blocked_on_input: 'bg-sky-500',
  qa_error: 'bg-pink-500',
  infra_error: 'bg-red-500',
  cancelled: 'bg-secondary-foreground/40',
  assessing: 'bg-muted-foreground/70',
  not_run: 'bg-muted-foreground/20',
};

/** Keep status badges in the detail header and pipeline visually consistent with outcome chips. */
const STATUS_TONE_CLASS: Partial<Record<AnyStatus, string>> = {
  unresolved: OUTCOME_CHIP_CLASS.unresolved,
  defect_found: OUTCOME_CHIP_CLASS.defect_found,
  case_unclear: OUTCOME_CHIP_CLASS.case_unclear,
  blocked_on_input: OUTCOME_CHIP_CLASS.blocked_on_input,
  qa_error: OUTCOME_CHIP_CLASS.qa_error,
  infra_error: OUTCOME_CHIP_CLASS.infra_error,
  assessing: OUTCOME_CHIP_CLASS.assessing,
  not_run: 'border-dashed border-border text-muted-foreground',
};

/**
 * Renders a status badge, tolerating a status this map doesn't know about (a newer server value
 * this client predates) — falls back to a neutral variant showing the raw status text, never an
 * empty badge.
 */
export function StatusBadge({
  status,
  className,
}: {
  status: AnyStatus;
  className?: string;
}): JSX.Element {
  const variant = Object.prototype.hasOwnProperty.call(STATUS_VARIANT, status)
    ? STATUS_VARIANT[status]
    : 'outline';
  /**
   * Own-property lookup, like the variant above. A plain index read walks the prototype chain, so a
   * status the server invents that happens to name an Object member ('toString', 'constructor')
   * would return a function — and `??` does not catch it, because a function is not nullish. React
   * then throws on rendering it. The point of tolerating unknown statuses is that they degrade to
   * text, so the lookup has to be as defensive as the variant one.
   */
  const label = Object.prototype.hasOwnProperty.call(STATUS_LABEL, status)
    ? STATUS_LABEL[status]
    : undefined;
  const tone = Object.prototype.hasOwnProperty.call(STATUS_TONE_CLASS, status)
    ? STATUS_TONE_CLASS[status]
    : undefined;
  return (
    <Badge
      variant={variant}
      className={cn(
        !label && 'capitalize',
        PULSING.has(status) && 'animate-pulse-soft',
        tone,
        className,
      )}
    >
      {label ?? status}
    </Badge>
  );
}

/** Primary execution badge for a canonical run, or the retained legacy badge and label. */
export function OutcomeRunBadge({
  run,
  summary,
}: {
  run: Run;
  summary?: RunSummary;
}): JSX.Element {
  const projection = outcomeProjection(run, summary);
  if (projection.kind === 'unsupported') {
    return <Badge variant="warning">Outcome unavailable</Badge>;
  }
  if (projection.kind === 'canonical') {
    return <StatusBadge status={projection.executionStatus} />;
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <StatusBadge status={run.badge_status ?? projection.status} />
      <Badge variant="outline">{projection.label}</Badge>
    </span>
  );
}

/** Shared outcome summary used by list/detail/report consumers. */
export function OutcomeResultsSummary({
  projection,
  compact = false,
  businessExecution = true,
  onReviewCases,
}: {
  projection: Extract<OutcomeProjection, { kind: 'canonical' }>;
  compact?: boolean;
  businessExecution?: boolean;
  /** Turns the "N cases require review" caption into a jump to the first case awaiting a decision. */
  onReviewCases?: () => void;
}): JSX.Element {
  if (!businessExecution)
    return (
      <div className="rounded-xl border border-border bg-card p-4 text-sm">
        <span>{projection.executionLabel}</span> · Work items:{' '}
        {projection.total}
        {projection.statusCaption ? <p>{projection.statusCaption}</p> : null}
        {projection.completionWarning === 'cleanup_unconfirmed' ? (
          <p className="text-warning-700">
            Cleanup could not be fully confirmed.
          </p>
        ) : null}
      </div>
    );
  const entries: Array<{
    id: string;
    label: string;
    count: number;
    tooltip: string | null;
    chipClass: string;
    segmentClass: string;
  }> = (
    Object.entries(projection.results) as [
      CaseOutcome | 'assessing' | 'not_run',
      number,
    ][]
  )
    .map(([outcome, count]) => ({
      id: outcome,
      label: outcomeLabel(outcome),
      count: outcome === 'qa_error' ? projection.confirmedQaErrors : count,
      tooltip: outcomeTooltip(outcome),
      chipClass: OUTCOME_CHIP_CLASS[outcome],
      segmentClass: OUTCOME_SEGMENT_CLASS[outcome],
    }))
    .filter(({ count }) => count > 0);
  if (projection.unconfirmedQaErrors > 0)
    entries.push({
      id: 'unconfirmed-qa',
      label: 'Unconfirmed assessment',
      count: projection.unconfirmedQaErrors,
      tooltip: outcomeTooltip('unconfirmed_assessment'),
      chipClass: OUTCOME_CHIP_CLASS['unconfirmed-qa'],
      segmentClass: 'bg-yellow-500',
    });
  const segments = [
    ...entries,
    ...(projection.pending > 0
      ? [
          {
            id: 'pending',
            label: 'Pending',
            count: projection.pending,
            segmentClass: 'bg-muted-foreground/40',
          },
        ]
      : []),
  ];
  const distributionDescription =
    segments.length > 0
      ? `Outcome distribution: ${segments
          .map(({ count, label }) => `${count} ${label}`)
          .join(', ')}.`
      : 'Outcome distribution: 0 outcomes, 0 pending.';
  const distributionTotal = projection.total > 0 ? projection.total : 0;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span
          data-testid="outcome-execution-count"
          className={cn(
            'font-heading font-bold tabular-nums',
            compact ? 'text-lg' : 'text-2xl',
          )}
        >
          {projection.executed}/{projection.total}
        </span>
        <span className="text-sm text-muted-foreground">executed</span>
        <span className="text-sm text-muted-foreground">
          {projection.pending} pending
        </span>
      </div>
      {entries.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {entries.map(({ id, label, count, tooltip, chipClass }) => {
            if (!tooltip)
              return (
                <Badge key={id} variant="outline" className={chipClass}>
                  {label} {count}
                </Badge>
              );
            return (
              <Tooltip key={id}>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className={chipClass} tabIndex={0}>
                    {label} {count}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>{tooltip}</TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      ) : null}
      <div
        role="img"
        aria-label={distributionDescription}
        data-testid="outcome-distribution"
        className={cn(
          'flex h-2 min-w-0 w-full overflow-hidden rounded-full bg-muted',
          compact ? 'my-0.5' : 'my-1',
        )}
      >
        {segments.map(({ id, count, segmentClass }) => {
          const width =
            distributionTotal > 0
              ? Math.min(100, Math.max(0, (count / distributionTotal) * 100))
              : 0;
          return (
            <span
              key={id}
              data-testid={`outcome-segment-${id}`}
              aria-hidden="true"
              className={cn('h-full shrink-0', segmentClass)}
              style={{ width: `${width}%` }}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {projection.statusCaption &&
        onReviewCases &&
        (projection.results.unresolved ?? 0) > 0 ? (
          <Button
            type="button"
            variant="link"
            className="h-auto rounded-sm p-0 text-xs font-normal"
            onClick={onReviewCases}
          >
            {projection.statusCaption}
          </Button>
        ) : projection.statusCaption ? (
          <span>{projection.statusCaption}</span>
        ) : null}
        {projection.completionWarning === 'cleanup_unconfirmed' ? (
          <span className="text-warning-700">
            Cleanup could not be fully confirmed.
          </span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Tone, label and explanation for a reaper-finalized run. Kept beside the status
 * maps so the list and detail header share one source of truth. The reaped reason
 * is metadata (why the safety net closed the run), not a status — render it as a
 * small secondary badge next to the status badge, never instead of it.
 */
const REAPED_VARIANT: Record<ReapedReason, BadgeVariant> = {
  never_started: 'warning',
  stalled: 'secondary',
};

const REAPED_LABEL: Record<ReapedReason, string> = {
  never_started: 'Never ran',
  stalled: 'Auto-closed',
};

const REAPED_TOOLTIP: Record<ReapedReason, string> = {
  never_started:
    'Auto-finalized while every case was still queued — this run never executed (likely starved waiting for a device session). The results below are not real test failures.',
  stalled:
    'The run went silent mid-execution and was finalized by the safety net.',
};

/**
 * Secondary badge shown next to the status badge when the safety-net reaper
 * finalized a run. Renders nothing for a normally-finalized run (`reaped_reason`
 * null/absent), so the common case is visually unchanged.
 */
export function ReapedReasonBadge({
  reason,
  className,
}: {
  reason: ReapedReason | null | undefined;
  className?: string;
}): JSX.Element | null {
  // Render nothing for a normal run, and also for an unknown reason from a newer server (the value
  // is cast from the API, not validated) rather than showing an empty/label-less badge.
  if (!reason || !REAPED_LABEL[reason]) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant={REAPED_VARIANT[reason]} className={className}>
          {REAPED_LABEL[reason]}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>{REAPED_TOOLTIP[reason]}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Wraps the status badge with a tooltip naming who cancelled the run, when the server recorded a
 * canceller (`cancelled_by`/`cancelled_by_name`). Copies the shape of `ReapedReasonBadge` but does
 * not extend that component: this is an annotation on the existing status badge, not a second
 * visible badge — cancellation already has exactly one visible state. Renders `children` unchanged
 * when there is no name, so every run predating this field, and a cancel with no viewer (the
 * worker/system path), keep today's plain badge with no empty tooltip shell. There is no separate
 * cancel timestamp on the wire — "when" is the run's existing `finished_at`, shown elsewhere on the
 * row. The trigger wraps `children` in a plain `tabIndex`-able `span` rather than putting `asChild`
 * directly on `StatusBadge`: `StatusBadge` doesn't forward arbitrary props/ref, so Radix's merged
 * pointer/focus handlers and `data-state` would be silently dropped and the tooltip would never open
 * (same reason `RunKindIcon` above wraps its icon in a `span` instead of using `asChild` on it). The
 * wrapper is `inline-flex`, not `display: contents` — a `contents` element generates no box, so
 * `getBoundingClientRect()` on it is all zeros, Floating UI anchors the tooltip at the viewport
 * origin instead of the badge, and a boxless element can fail to take focus at all.
 */
export function CancelReasonTooltip({
  cancelledByName,
  children,
}: {
  cancelledByName?: string | null;
  children: JSX.Element;
}): JSX.Element {
  if (!cancelledByName) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="inline-flex">
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent>{`Cancelled by ${cancelledByName}`}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Secondary badge shown next to the status badge when the run holds an unsettled, non-expired
 * help request (spec F1) — one badge, no kind distinction, no count. Renders nothing for a run
 * with nothing pending, and also for `needs_help` absent entirely (a server predating the field),
 * so the common case is visually unchanged.
 */
export function NeedsHelpBadge({
  needsHelp,
  className,
}: {
  needsHelp: boolean | null | undefined;
  className?: string;
}): JSX.Element | null {
  if (!needsHelp) return null;
  return (
    <Badge variant="warning" className={className}>
      Needs help
    </Badge>
  );
}

/**
 * English sentence per case-termination reason code (mirror of cti-quality-service `ReasonCode`).
 * The SPA owns the wording — keep in step with the service-side `REASON_TEXT` map (both are the
 * download-surface twins of this). Unknown code → a safe generic, never blank. Exported so a test
 * can assert this map carries every key the service's `ReasonCode` union declares (see
 * `quality-presentation.reason-parity.test.ts`) — the drift between the two was itself a bug.
 * `satisfies Readonly<Record<...>>` because this is now a shared source of truth read by that
 * test: the type itself should stop an accidental mutation at authoring time, not just at runtime.
 * `satisfies` (rather than a `: Readonly<Record<string, string>>` annotation) keeps the literal key
 * union inferred from the object below — widening to `Record<string, string>` would turn every
 * indexed read into `string | undefined` under this repo's `noUncheckedIndexedAccess`.
 */
export const REASON_CODE_TEXT = {
  scheduling_exhausted:
    'Could not be scheduled — re-queued too many times without ever starting.',
  run_stalled:
    'The run stalled after a case started and was auto-closed before this case finished.',
  run_never_started:
    'The run was auto-closed because no case ever started running.',
  no_browserstack_account:
    "The project's BrowserStack account was unavailable, so the case could not run.",
  no_device_farm_account:
    'The instance device farm was unavailable (hub/credentials not configured for this environment), so the case could not run.',
  shard_enqueue_failed:
    'The case could not be queued for execution (internal error).',
  run_over: 'The run had already finished, so this case was not executed.',
  shard_crashed:
    'The worker running this case crashed before it reported a result.',
  awaiting_input:
    'The agent asked for a value (login, code, or other input) and no answer arrived in time.',
  auth_input_expired:
    'Authentication input expired, so the remaining cases were stopped and the run failed.',
  harness_incompatibility:
    'The selected test harness is incompatible with this run, so the case could not execute.',
  no_verdict:
    'The case stopped before the test reported a verdict, so its result could not be trusted.',
  browser_crashed:
    'The browser died mid-case, so the case could not finish and was re-run in a fresh session.',
  preflight_failed:
    'The run failed its pre-flight check (a required credential is missing or the BrowserStack account is unavailable), so the case never ran.',
  browserstack_capacity:
    'The BrowserStack account had no free parallel session for too long, so the case was given up on.',
  active_budget_exhausted:
    'The run used its active-time budget before this case could finish, so the case was stopped without a trusted verdict.',
  executor_no_progress:
    'The executor stopped making progress for too long, so the case was stopped without a trusted verdict.',
  quality_infrastructure:
    'Quality infrastructure failed while running this case, so the result could not be trusted.',
  device_lease_unavailable:
    "No device was free on the farm within the project's wait limit, so the case could not run.",
  device_lease_revoked:
    'An operator released the device this run was using, so the case could not run.',
  zero_test_steps:
    'The case reached the executor with no steps to run, so nothing was tested. Check that its steps are authored in the test management system.',
  model_provider_error:
    'The AI model provider had a temporary outage, so the case stopped before it finished. Your test and application are not at fault. When the run finishes, press Restart failed to run it again.',
  iteration_not_run:
    'Skipped on purpose: an earlier iteration failed and the run was set to stop. Nothing broke.',
  credential_delivery_blocked:
    "Login was blocked before the credential reached the application, so the application was not tested. The case error says why. If the login page is on an address the credential does not list, add that address to the credential's Advanced settings → Additional login addresses, then run again.",
} as const satisfies Readonly<Record<string, string>>;

// Only NO-VERDICT terminal statuses inherit the run's reaped reason: 'broken' (started then crashed)
// and 'timeout' (ran out of budget, no verdict). This mirrors the service's report derivation
// (cti-quality-service report.service.ts reasonForRow) EXACTLY — 'failed' is a genuine verdict whose
// detail is its own error, and 'cancelled' is a non-reason terminal the service does not derive for;
// including either would drift the live UI from the downloadable report.
const TERMINAL_NO_VERDICT = new Set(['broken', 'timeout']);

export function hasKnownCaseReasonCode(
  reasonCode: string | null | undefined,
): boolean {
  return (
    typeof reasonCode === 'string' &&
    Object.prototype.hasOwnProperty.call(REASON_CODE_TEXT, reasonCode)
  );
}

/**
 * Human sentence for WHY a case ended without a normal verdict, or null when it carries no reason
 * (passed / still running / a genuine failure whose detail is its own error message). Precedence: the
 * row's persisted `reason_code` wins; else, for a terminated case, the run's reaped reason derives one
 * — this surfaces a reason on runs written before the reason_code column (no backfill), mirroring the
 * service's report derivation so the live UI and the downloadable report agree.
 */
export function caseReasonText(
  result: { reason_code?: string | null; status: string },
  reapedReason: ReapedReason | null | undefined,
): string | null {
  if (result.reason_code) {
    // Own-property guard: reason_code is a type-cast server string, so a hostile/unexpected key
    // (`__proto__`, `constructor`, `toString`) must not resolve up the prototype chain to a
    // non-string and crash React on render — those fall back to the safe generic sentence.
    const code = result.reason_code;
    return Object.prototype.hasOwnProperty.call(REASON_CODE_TEXT, code)
      ? REASON_CODE_TEXT[code as keyof typeof REASON_CODE_TEXT]
      : 'The case did not produce a result.';
  }
  if (TERMINAL_NO_VERDICT.has(result.status) && reapedReason) {
    if (reapedReason === 'stalled') return REASON_CODE_TEXT.run_stalled;
    if (reapedReason === 'never_started')
      return REASON_CODE_TEXT.run_never_started;
  }
  return null;
}

export function runReapedReasonText(
  reapedReason: ReapedReason | null | undefined,
): string | null {
  if (reapedReason === 'stalled') return REASON_CODE_TEXT.run_stalled;
  if (reapedReason === 'never_started')
    return REASON_CODE_TEXT.run_never_started;
  return null;
}

/**
 * Visual identity per run family: a calm icon + token-backed text tone (no raw colours). The icon
 * conveys the coarse family at a glance in the runs list (the tooltip carries the precise kind).
 */
const FAMILY_META: Record<RunFamily, { Icon: LucideIcon; tone: string }> = {
  test: { Icon: ClipboardList, tone: 'text-info-600' },
  generate: { Icon: Sparkles, tone: 'text-primary' },
  explore: { Icon: Telescope, tone: 'text-warning-600' },
};

/**
 * Icon-only run-kind marker shown before the run name: the family icon (ClipboardList / Sparkles / Telescope),
 * tinted by family, with the precise kind in a tooltip. Absent `run_kind` reads as the 'test' family
 * (legacy runs are executions); the name itself still falls back to "Run #N" via runName().
 */
export function RunKindIcon({
  kind,
  className,
}: {
  kind?: Run['run_kind'];
  className?: string;
}): JSX.Element {
  const { Icon, tone } = FAMILY_META[runFamily(kind)];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn('inline-flex shrink-0 items-center', tone, className)}
        >
          <Icon className="size-4" aria-hidden />
        </span>
      </TooltipTrigger>
      <TooltipContent>{runKindLabel(kind)}</TooltipContent>
    </Tooltip>
  );
}

const PLATFORM_LABEL: Record<Platform, string> = {
  web: 'Web',
  mobile: 'Mobile',
};

/**
 * Display label for a run's target. The scalar `platform` is the derived PRIMARY (mobile wins when a
 * run spans both), so a cross-platform run would read as just "Mobile". When `components` names more
 * than one, list them all ("Web + Mobile") in a stable order; otherwise fall back to the platform.
 */
export function platformLabel(
  platform: Platform,
  components?: string[] | null,
): string {
  // Trust `components` whenever the server names them (cast from the API, so dedupe) — including a
  // single non-platform component (api/db) the scalar `platform` couldn't represent. Both lookup
  // tables are read with an own-property guard: the key is cast from the API, so a prototype-chain key
  // like `__proto__` must fall back to the raw string / default rank, not resolve up the chain.
  const order: Record<string, number> = { web: 0, mobile: 1 };
  const rankOf = (component: string): number =>
    Object.prototype.hasOwnProperty.call(order, component)
      ? (order[component] ?? 9)
      : 9;
  const textOf = (component: string): string =>
    Object.prototype.hasOwnProperty.call(PLATFORM_LABEL, component)
      ? (PLATFORM_LABEL[component as Platform] ?? component)
      : component;
  const unique = components ? [...new Set(components)] : [];
  if (unique.length >= 1) {
    return unique
      .sort((first, second) => rankOf(first) - rankOf(second))
      .map(textOf)
      .join(' + ');
  }
  return Object.prototype.hasOwnProperty.call(PLATFORM_LABEL, platform)
    ? (PLATFORM_LABEL[platform] ?? 'Unknown')
    : 'Unknown';
}

const PLATFORM_ICON: Record<string, LucideIcon> = {
  web: Globe,
  mobile: Smartphone,
};

/** The lucide icon for a single platform component (globe = web, smartphone = mobile). */
export function platformComponentIcon(component?: string): LucideIcon {
  // `component` is cast from the API — guard with an own-property check so a prototype-chain key like
  // `__proto__` can't resolve to a non-icon value and crash the render.
  if (
    component &&
    Object.prototype.hasOwnProperty.call(PLATFORM_ICON, component)
  ) {
    return PLATFORM_ICON[component] ?? Box;
  }
  return Box;
}

/**
 * Icon marker for a run's target platform: a globe (web) and/or a smartphone (mobile), with the
 * label in a tooltip. A cross-platform run (more than one component) shows one icon per component in
 * a stable order; a single-component/legacy run shows the single platform icon.
 */
export function PlatformIcon({
  platform,
  components,
  className,
}: {
  platform: Platform;
  components?: string[] | null;
  className?: string;
}): JSX.Element {
  const order: Record<string, number> = { web: 0, mobile: 1 };
  // Own-property guard: `components` is cast from the API, so a prototype-chain key must rank as the
  // default (9), not resolve `order.__proto__` to a non-number and yield a NaN comparison.
  const rankOf = (component: string): number =>
    Object.prototype.hasOwnProperty.call(order, component)
      ? (order[component] ?? 9)
      : 9;
  // Dedupe (cast from the API) and render the component list whenever present — incl. a single
  // non-platform component; fall back to the scalar platform only when components is absent/empty.
  const unique = components ? [...new Set(components)] : [];
  const shown =
    unique.length >= 1
      ? unique.sort((first, second) => rankOf(first) - rankOf(second))
      : [platform];
  const label = platformLabel(platform, components);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex shrink-0 items-center gap-1 text-muted-foreground',
            className,
          )}
          role="img"
          aria-label={label}
          tabIndex={0}
        >
          {shown.map((component) => {
            const Icon = platformComponentIcon(component);
            return <Icon key={component} className="size-4" aria-hidden />;
          })}
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/** Coerce a possibly-string numeric field and format milliseconds. An hour or more drops seconds
 *  and reads as hours + minutes (e.g. "5h 12m") so long QA stages and cases stay legible. */
export function formatDuration(
  value: number | string | null | undefined,
): string {
  if (value === null || value === undefined) return '—';
  const ms = Number(value);
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  if (ms < 1_000) return `${Math.round(ms)} ms`;
  const seconds = ms / 1_000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  // Round to whole seconds ONCE, then derive m/s (or h/m) from that, so a
  // non-integer ms diff can never leave a 60s remainder (e.g. "1m 60s") or roll
  // a near-hour into "60m 0s".
  const totalSec = Math.round(seconds);
  if (totalSec < 3_600) {
    const minutes = Math.floor(totalSec / 60);
    const rem = totalSec % 60;
    return `${minutes}m ${rem}s`;
  }
  const hours = Math.floor(totalSec / 3_600);
  const minutes = Math.floor((totalSec % 3_600) / 60);
  return `${hours}h ${minutes}m`;
}

/** Coerce a `TestResult.deviation_count` field to a non-negative integer, since the field is
 *  type-cast from the API response rather than validated (`(await res.json()) as T`) and an older
 *  or misbehaving server can send a non-number such as `"0"` or a fractional value. A non-numeric
 *  or negative value reads as 0 rather than rendering a wrong badge or poisoning a run-level sum;
 *  a fractional value truncates, since this is a count of steps and cannot be fractional. */
export function deviationCount(
  value: number | string | null | undefined,
): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
}

/** A video seek offset (ms) as m:ss / h:mm:ss — used by step "jump to video" chips. */
export function formatTimecode(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  const total = Math.floor(n / 1000);
  const s = total % 60;
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);
  const pad = (x: number): string => String(x).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** True once a recognized legacy or canonical lifecycle reaches a terminal state. */
export function isTerminalRun(status: RunStatus | ExecutionStatus): boolean {
  return (
    status === 'passed' ||
    status === 'failed' ||
    status === 'incomplete' ||
    status === 'cancelled' ||
    status === 'completed' ||
    status === 'partially_completed' ||
    status === 'not_completed'
  );
}

type SummaryLike = Pick<RunSummary, 'total' | 'passed' | 'failed' | 'pending'> &
  Partial<Pick<RunSummary, 'broken' | 'timeout'>>;

type NormalizedRunSummary = {
  total: number;
  passed: number;
  failed: number;
  broken: number;
  timeout: number;
  cancelled: number;
  pending: number;
};

function countStatus(results: TestResult[], status: ResultStatus): number {
  return results.filter((result) => result.status === status).length;
}

/**
 * Display counts for progress UI. Older or transitional service responses may still leave
 * terminal no-verdict cases (`broken`/`timeout`) in `pending`; when case results are present they
 * are the sharper source of truth, and otherwise the split summary buckets still count as done.
 */
export function normalizeRunSummary(
  summary: SummaryLike,
  results?: TestResult[],
  options: { inferCancelledResidual?: boolean } = {},
): NormalizedRunSummary {
  if (results && results.length > 0) {
    const total = Math.max(summary.total, results.length);
    const passed = countStatus(results, 'passed');
    const failed = countStatus(results, 'failed');
    const broken = countStatus(results, 'broken');
    const timeout = countStatus(results, 'timeout');
    const explicitCancelled = countStatus(results, 'cancelled');
    const omitted = Math.max(total - results.length, 0);
    const cancelled =
      explicitCancelled + (options.inferCancelledResidual ? omitted : 0);
    const done = passed + failed + broken + timeout + cancelled;
    return {
      total,
      passed,
      failed,
      broken,
      timeout,
      cancelled,
      pending: Math.max(total - done, 0),
    };
  }

  const total = summary.total;
  const passed = summary.passed;
  const failed = summary.failed;
  const broken = summary.broken ?? 0;
  const timeout = summary.timeout ?? 0;
  const done = passed + failed + broken + timeout;
  const remaining = Math.max(total - done, 0);
  const pending = options.inferCancelledResidual
    ? 0
    : Math.max(Math.min(summary.pending, remaining), 0);
  const residual = Math.max(remaining - pending, 0);
  const cancelled = options.inferCancelledResidual ? remaining : 0;
  return {
    total,
    passed,
    failed,
    broken,
    timeout,
    cancelled,
    pending: pending + (options.inferCancelledResidual ? 0 : residual),
  };
}

/**
 * The status a caller should render as the badge for a run. cti-quality-service's `runVerdict()` /
 * `badgeStatus()` is the sole authority for this value — it is derived server-side from the run's
 * test-result counts, never from a marker or counter here. `run.badge_status` is absent only when
 * talking to a server that predates the field, in which case `run.status` is already correct.
 */
export function badgeStatus(run: {
  status: RunStatus;
  badge_status?: RunStatus;
}): RunStatus {
  return run.badge_status ?? run.status;
}
